import { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDownIcon, ChevronRightIcon, Cog6ToothIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import CannotDeleteConfigurationDialog from './CannotDeleteConfigurationDialog';
import DeleteConfigurationDialog from './DeleteConfigurationDialog';
import SaveConfigurationModal from './SaveConfigurationModal';

function ContinentSection({ continent, sources }) {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="flex w-full items-center gap-1.5 pl-5 pr-3 py-1 text-xs text-gray-500 hover:bg-gray-50"
      >
        {isOpen ? (
          <ChevronDownIcon className="w-3 h-3 text-gray-400" />
        ) : (
          <ChevronRightIcon className="w-3 h-3 text-gray-400" />
        )}
        {continent ?? 'Other'}
        <span className="ml-auto text-[10px] text-gray-300">{sources.length}</span>
      </button>
      {isOpen && (
        <table className="table table-sm">
          <tbody>
            {sources.map(source => (
              <tr key={source}>
                <td className="pl-9 font-medium">{source}</td>
                <td>Sesame</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

ContinentSection.propTypes = {
  continent: PropTypes.string,
  sources: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function VersionSection({ dataset_version, continents, is_latest }) {
  const [isOpen, setIsOpen] = useState(is_latest);
  const totalSources = continents.reduce((sum, c) => sum + c.sources.length, 0);
  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="flex w-full items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
          ) : (
            <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
          )}
          <span>{dataset_version ?? 'Default'}</span>
        </div>
        <span className="text-xs font-normal text-gray-400">
          {totalSources} source{totalSources !== 1 ? 's' : ''}
        </span>
      </button>
      {isOpen &&
        continents.map(({ continent, sources }) => (
          <ContinentSection key={continent} continent={continent} sources={sources} />
        ))}
    </div>
  );
}

VersionSection.propTypes = {
  dataset_version: PropTypes.string,
  continents: PropTypes.arrayOf(
    PropTypes.shape({
      continent: PropTypes.string,
      sources: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ).isRequired,
  is_latest: PropTypes.bool.isRequired,
};

const ManageConfigurationsModal = ({
  show,
  availableSourcesByDatasetVersion,
  availableConfigurations,
  onEdit,
  onDelete,
  onClose,
}) => {
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [cannotDeleteReasons, setCannotDeleteReasons] = useState([]);

  if (!show) {
    return null;
  }

  const handleDeleteClick = config => {
    setConfirmConfig(config);
  };

  const handleConfirmDelete = async () => {
    setDeletingId(confirmConfig.id);
    try {
      await onDelete(confirmConfig.id);
      setConfirmConfig(null);
    } catch (error) {
      const reasons = Array.isArray(error?.response?.data) ? error.response.data : ['Unable to delete configuration.'];
      setConfirmConfig(null);
      setCannotDeleteReasons(reasons);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditConfiguration = async values => {
    setEditingId(editingConfig.id);
    try {
      await onEdit(editingConfig.id, values);
      setEditingConfig(null);
    } finally {
      setEditingId(null);
    }
  };

  if (cannotDeleteReasons.length > 0) {
    return (
      <CannotDeleteConfigurationDialog show reasons={cannotDeleteReasons} onClose={() => setCannotDeleteReasons([])} />
    );
  }

  if (confirmConfig) {
    return (
      <DeleteConfigurationDialog
        show
        configName={confirmConfig.name}
        isDeleting={deletingId === confirmConfig.id}
        onCancel={() => setConfirmConfig(null)}
        onDelete={handleConfirmDelete}
      />
    );
  }

  if (editingConfig) {
    return (
      <SaveConfigurationModal
        key={editingConfig.id}
        show
        mode="edit"
        initialName={editingConfig.name}
        initialPermissions={editingConfig.permissions || 'project'}
        isLoading={editingId === editingConfig.id}
        onCancel={() => setEditingConfig(null)}
        onSave={handleEditConfiguration}
      />
    );
  }

  return (
    <dialog open={show} className="modal">
      <div className="modal-box max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gray-100 p-2 rounded-full">
            <Cog6ToothIcon className="w-6 h-6 text-gray-600" />
          </div>
          <h3 className="font-bold text-lg">Manage configurations</h3>
        </div>
        <p className="text-gray-600 mb-6">
          View and manage data configurations for this module. Custom configurations can be deleted if not used
          elsewhere.
        </p>
        <div className="space-y-2">
          {availableSourcesByDatasetVersion.map(group => (
            <VersionSection key={group.dataset_version} {...group} />
          ))}
          {availableConfigurations.length > 0 && (
            <>
              <div className="pt-2 text-xs font-medium uppercase tracking-wide text-gray-400">Saved configurations</div>
              <table className="table table-sm">
                <thead>
                  <tr className="text-gray-500">
                    <th>Name</th>
                    <th>Created by</th>
                    <th>Permissions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {availableConfigurations.map(config => (
                    <tr key={config.id}>
                      <td className="font-medium">{config.name}</td>
                      <td>{config.creator === 'self' ? 'You' : config.creator}</td>
                      <td className="capitalize">{config.permissions}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => setEditingConfig(config)}
                          className="btn btn-ghost btn-xs btn-circle"
                          title="Edit configuration"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(config)}
                          className="btn btn-ghost btn-xs btn-circle"
                          title="Delete configuration"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className="modal-action">
          <button type="button" onClick={onClose} className="btn btn-sm btn-outline">
            Close
          </button>
        </div>
      </div>
      <div className="modal-backdrop bg-black/50" onClick={onClose}></div>
    </dialog>
  );
};

ManageConfigurationsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  availableSourcesByDatasetVersion: PropTypes.arrayOf(
    PropTypes.shape({
      dataset_version: PropTypes.string,
      continents: PropTypes.arrayOf(
        PropTypes.shape({
          continent: PropTypes.string,
          sources: PropTypes.arrayOf(PropTypes.string).isRequired,
        }),
      ).isRequired,
      is_latest: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  availableConfigurations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      creator: PropTypes.string,
      permissions: PropTypes.string,
    }),
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ManageConfigurationsModal;
